/** @format */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import galaxieClient from "@/api/galaxieClient";
import { useUser } from "@/api/userAPI";
import { useNotificationsEnabled } from "@/hooks/useStableFeatureFlags";
import { trackEvent } from "@/utils/analytics";

// Module-level guard: only one pushManager.subscribe() at a time across all hook instances
let subscribeInFlight = false;

interface PushSubscriptionData {
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  userAgent: string;
  browser: string;
  platform: string;
}

interface VapidResponse {
  publicKey: string;
  enabled: boolean;
}

interface PushSubscriptionRecord {
  id: string;
  endpoint: string;
  browser: string;
  platform: string;
  label: string;
  lastUsedAt: string;
  createdAt: string;
}

/**
 * Hook for managing Web Push notification subscriptions.
 */
export function usePushNotifications() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const notificationsEnabled = useNotificationsEnabled();
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get VAPID public key (doesn't require auth)
  const { data: vapidData } = useQuery<VapidResponse>({
    queryKey: ["vapid-public-key"],
    queryFn: async () => {
      const response = await galaxieClient.get<VapidResponse>("/notifications/vapid/");
      return response.data;
    },
    staleTime: Infinity, // VAPID key doesn't change
  });

  // Get current subscriptions (requires auth + notifications feature flag)
  const { data: existingSubscriptions } = useQuery<PushSubscriptionRecord[]>({
    queryKey: ["push-subscriptions"],
    queryFn: async () => {
      const response = await galaxieClient.get<PushSubscriptionRecord[]>(
        "/notifications/subscribe/"
      );
      return response.data;
    },
    enabled: !!user?.id && permission === "granted" && notificationsEnabled,
  });

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: async (subscriptionData: PushSubscriptionData) => {
      const response = await galaxieClient.post<PushSubscriptionRecord>(
        "/notifications/subscribe/",
        subscriptionData
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["push-subscriptions"] });
    },
  });

  // Unsubscribe mutation
  const unsubscribeMutation = useMutation({
    mutationFn: async (endpoint: string) => {
      const response = await galaxieClient.delete<void>("/notifications/subscribe/", {
        data: { endpoint },
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["push-subscriptions"] });
    },
  });

  // Check permission status on mount
  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const refreshPermissionStatus = useCallback(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Restore subscription from service worker if permission is already granted,
  // and validate it against backend subscriptions
  useEffect(() => {
    const restoreSubscription = async () => {
      if (permission === "granted" && "serviceWorker" in navigator && !subscription) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSub = await registration.pushManager.getSubscription();
          if (existingSub) {
            setSubscription(existingSub);

            // If backend subscriptions are loaded and this browser's endpoint
            // isn't registered, re-register so the backend can deliver to it
            if (
              existingSubscriptions !== undefined &&
              !existingSubscriptions.some(s => s.endpoint === existingSub.endpoint) &&
              vapidData?.publicKey
            ) {
              const key = existingSub.getKey("p256dh");
              const auth = existingSub.getKey("auth");
              if (key && auth) {
                subscribeMutation.mutate(
                  {
                    endpoint: existingSub.endpoint,
                    p256dhKey: arrayBufferToBase64(key),
                    authKey: arrayBufferToBase64(auth),
                    userAgent: navigator.userAgent,
                    browser: detectBrowser(navigator.userAgent),
                    platform: detectPlatform(),
                  },
                  {
                    onError: err => {
                      console.error("[Push] Failed to re-register subscription:", err);
                    },
                  }
                );
              }
            }
          }
        } catch (err) {
          console.error("[Push] Error restoring subscription:", err);
        }
      }
    };

    void restoreSubscription();
  }, [permission, subscription, existingSubscriptions, vapidData, subscribeMutation]);

  /**
   * Request notification permission and subscribe to push.
   */
  const requestPermissionAndSubscribe = useCallback(
    async (options?: {
      onPermissionGranted?: () => void;
      source?: "header_banner" | "settings";
    }): Promise<boolean> => {
      const source = options?.source ?? "header_banner";
      let permissionGranted = permission === "granted";
      let didNotifyPermissionGranted = false;

      const notifyPermissionGranted = () => {
        if (didNotifyPermissionGranted) {
          return;
        }
        didNotifyPermissionGranted = true;
        options?.onPermissionGranted?.();
      };

      try {
        if (subscribeInFlight) {
          return permissionGranted;
        }
        subscribeInFlight = true;
        setError(null);
        setIsLoading(true);

        // Check browser support
        if (!("Notification" in window)) {
          throw new Error("This browser does not support notifications");
        }

        if (!("serviceWorker" in navigator)) {
          throw new Error("This browser does not support service workers");
        }

        if (!("PushManager" in window)) {
          throw new Error("This browser does not support push notifications");
        }

        // Check if VAPID key is loaded
        if (!vapidData?.publicKey) {
          throw new Error("VAPID public key not loaded yet. Please try again.");
        }

        const perm = await Notification.requestPermission();
        setPermission(perm);
        trackEvent("notification_permission_outcome", {
          permission: perm,
          source,
        });
        permissionGranted = perm === "granted";
        if (permissionGranted) {
          notifyPermissionGranted();
        }

        if (perm !== "granted") {
          throw new Error("Notification permission denied");
        }

        // First, check if service worker is registered
        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          const swUrl =
            import.meta.env.MODE === "development"
              ? "/sw-dev.js"
              : "/service-worker.js";

          try {
            registration = await navigator.serviceWorker.register(swUrl, {
              scope: "/",
            });
          } catch (swError) {
            const swMessage =
              swError instanceof Error ? swError.message : "Unknown error";
            if (swMessage.includes("MIME type") || swMessage.includes("text/html")) {
              throw new Error(
                `Service worker file not found. Please restart the dev server to enable service worker support. Error: ${swMessage}`
              );
            }
            throw new Error(`Failed to register service worker: ${swMessage}`);
          }
        }

        // Wait for service worker to be ready (with timeout)
        const readyTimeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Service worker ready timeout")), 10000)
        );

        registration = await Promise.race([
          navigator.serviceWorker.ready,
          readyTimeout,
        ]);

        // Check if already subscribed
        let pushSubscription = await registration.pushManager.getSubscription();

        if (!pushSubscription) {
          const appServerKey = urlBase64ToArrayBuffer(vapidData.publicKey);
          if (appServerKey.byteLength !== 65) {
            throw new Error(
              `Invalid VAPID public key: expected 65 bytes, got ${appServerKey.byteLength}. ` +
                "Regenerate keys with generate_vapid_keys.py"
            );
          }

          // pushManager.subscribe() can hang silently on invalid keys — add a timeout
          const subscribePromise = registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appServerKey,
          });
          const subscribeTimeout = new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("Push subscription timed out after 15s")),
              15000
            )
          );
          pushSubscription = await Promise.race([subscribePromise, subscribeTimeout]);
        }

        if (pushSubscription) {
          // Extract keys
          const key = pushSubscription.getKey("p256dh");
          const auth = pushSubscription.getKey("auth");

          if (!key || !auth) {
            throw new Error("Failed to get subscription keys");
          }

          // Detect browser and platform
          const userAgent = navigator.userAgent;
          const browser = detectBrowser(userAgent);
          const platform = detectPlatform();

          // Send to backend first, then update local state on success
          await subscribeMutation.mutateAsync({
            endpoint: pushSubscription.endpoint,
            p256dhKey: arrayBufferToBase64(key),
            authKey: arrayBufferToBase64(auth),
            userAgent,
            browser,
            platform,
          });

          // Only set subscription state after backend confirms
          setSubscription(pushSubscription);

          // Invalidate queries to refresh subscription status
          void queryClient.invalidateQueries({
            queryKey: ["push-subscriptions"],
          });

          return true;
        }

        return permissionGranted;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        setError(message);
        trackEvent("notification_subscribe_failed", { message, source });
        console.error("[Push] Subscription error:", err);
        if (err instanceof Error) {
          console.error("[Push] Error stack:", err.stack);
        }
        return permissionGranted;
      } finally {
        subscribeInFlight = false;
        setIsLoading(false);
      }
    },
    [permission, vapidData, subscribeMutation, queryClient]
  );

  /**
   * Unsubscribe from push notifications.
   */
  const unsubscribe = useCallback(async () => {
    try {
      setError(null);

      if (!subscription) {
        return;
      }

      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Notify backend
      await unsubscribeMutation.mutateAsync(subscription.endpoint);

      setSubscription(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      console.error("Push unsubscribe error:", err);
    }
  }, [subscription, unsubscribeMutation]);

  // Check if THIS browser's endpoint is registered with the backend
  const isCurrentBrowserRegistered = useMemo(() => {
    if (!subscription || !existingSubscriptions) return false;
    return existingSubscriptions.some(s => s.endpoint === subscription.endpoint);
  }, [subscription, existingSubscriptions]);

  // Listen for service worker messages about subscription changes
  // (e.g., pushsubscriptionchange event from the SW)
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
        console.info("[Push] Subscription changed by push service, re-subscribing...");
        void requestPermissionAndSubscribe();
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [requestPermissionAndSubscribe]);

  return {
    permission,
    isSubscribed:
      permission === "granted" &&
      // Trust backend confirmation, or local subscription while backend list is loading
      (isCurrentBrowserRegistered ||
        (!!subscription && existingSubscriptions === undefined)),
    subscription,
    error,
    isLoading,
    isSupported: "Notification" in window && "serviceWorker" in navigator,
    isEnabled: vapidData?.enabled ?? false,
    refreshPermissionStatus,
    requestPermissionAndSubscribe,
    unsubscribe,
  };
}

/**
 * Hook for managing device subscriptions (list, rename, remove).
 */
export function useDeviceSubscriptions() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const notificationsEnabled = useNotificationsEnabled();

  const { data: devices, isLoading } = useQuery<PushSubscriptionRecord[]>({
    queryKey: ["push-subscriptions"],
    queryFn: async () => {
      const response = await galaxieClient.get<PushSubscriptionRecord[]>(
        "/notifications/subscribe/"
      );
      return response.data;
    },
    enabled: !!user?.id && notificationsEnabled,
  });

  const renameMutation = useMutation({
    mutationFn: async ({ id, label }: { id: string; label: string }) => {
      const response = await galaxieClient.patch(`/notifications/subscribe/${id}/`, {
        label,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["push-subscriptions"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await galaxieClient.delete(`/notifications/subscribe/${id}/`);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["push-subscriptions"] });
    },
  });

  return {
    devices: devices ?? [],
    isLoading,
    renameDevice: renameMutation.mutate,
    deleteDevice: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
}

// Utility functions

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function detectBrowser(userAgent: string): string {
  if (userAgent.includes("Firefox")) {
    return "Firefox";
  }
  if (userAgent.includes("Edg")) {
    return "Edge";
  }
  if (userAgent.includes("Chrome")) {
    return "Chrome";
  }
  if (userAgent.includes("Safari")) {
    return "Safari";
  }
  return "Unknown";
}

function detectPlatform(): string {
  if (/Android/.test(navigator.userAgent)) {
    return "mobile";
  }
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
    return "mobile";
  }
  return "desktop";
}
